import {
  type SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import {
  ArrowCounterClockwiseIcon,
  FilterDrawer,
  XCircleIcon,
  ListBulletsIcon,
  MapTrifoldIcon,
  Mtds,
  SampleStatusEnum,
  TopBar,
  AppLayout,
  getEmptySampleFilters,
  getFilterCount,
  getTaskTitle,
  useFilters,
  useGetPlanByIdQuery,
  useGetSamplesQuery,
  useGetTaskByIdQuery,
  useAllSamples,
  useSampleIds,
  OwnerId,
  type Filters,
  GetSamplesQueryParams,
  ScreenHeader,
  SamplesView,
  EmptyState,
  SampleFilterForm,
  resultFilterOptions
} from 'prove-common'
import { SampleFilterChips } from '../components/sample-filter-chips/SampleFilterChips'
import { SideBarLayout } from '../components/side-bar-layout/SideBarLayout'
import type { PaginationState, SortingState } from '@tanstack/react-table'
import { Link as RouterLink, useLocation, useNavigate } from 'react-router'
import { MapSamplesView } from '../components/map-samples-view/MapSamplesView'
import { AppLogo } from '../components/app-logo/AppLogo'

type SamplesScreenView = 'table' | 'map'

const hasOrgFilterValue = (value?: string): value is string =>
  value != null && value.length > 0

const getOwnerIdsFromFilters = (
  filters: Filters<SampleStatusEnum>
): OwnerId[] | undefined => {
  if (hasOrgFilterValue(filters.teamId)) {
    return [new OwnerId('team', filters.teamId)]
  }
  if (hasOrgFilterValue(filters.seksjonId)) {
    return [new OwnerId('seksjon', filters.seksjonId)]
  }
  if (hasOrgFilterValue(filters.avdelingId)) {
    return [new OwnerId('avdeling', filters.avdelingId)]
  }
  if (hasOrgFilterValue(filters.regionId)) {
    return [new OwnerId('region', filters.regionId)]
  }
  if (hasOrgFilterValue(filters.ownerId)) {
    const ownerId = OwnerId.fromString(filters.ownerId)
    return ownerId ? [ownerId] : undefined
  }
  return undefined
}

export default function SamplesScreen() {
  const navigate = useNavigate()
  const location = useLocation()

  const queryParams = new URLSearchParams(location.search)
  const pageIndex = Number(queryParams.get('pageIndex') || 0)
  const pageSize = Number(queryParams.get('pageSize') || 20)
  const sortId = queryParams.get('sortId') || 'requisition.createdDate'
  const sortDesc = (queryParams.get('sortDesc') || 'true') === 'true'
  const urlTabIndex = Number(queryParams.get('tabIndex') || 0)
  const urlPlanIds = queryParams.getAll('planIds')
  const urlTaskIds = queryParams.getAll('taskIds')
  const resultParam = queryParams.get('result')
  const urlResult = resultFilterOptions.find(
    ({ const: result }) => result === resultParam
  )?.const
  const urlPlanIdsKey = urlPlanIds.join(',')
  const urlTaskIdsKey = urlTaskIds.join(',')

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex,
    pageSize
  })
  const [sorting, setSorting] = useState<SortingState>([
    {
      id: sortId,
      desc: sortDesc
    }
  ])
  const [screenView, setScreenView] = useState<SamplesScreenView>(
    urlTabIndex === 1 ? 'map' : 'table'
  )
  const tabIndex = screenView === 'map' ? 1 : 0

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    params.set('pageIndex', pagination.pageIndex.toString())
    params.set('pageSize', pagination.pageSize.toString())
    params.set('tabIndex', tabIndex.toString())
    if (sorting.length === 1) {
      params.set('sortId', sorting[0].id)
      params.set('sortDesc', sorting[0].desc.toString())
    } else {
      params.delete('sortId')
      params.delete('sortDesc')
    }
    navigate({ search: params.toString() }, { replace: true })
  }, [pagination, sorting, tabIndex])

  const {
    filters,
    setFilters,
    isReady: isFiltersReady
  } = useFilters<SampleStatusEnum>('filterPlanSamples')
  const contextFilters: Pick<
    Filters<SampleStatusEnum>,
    'planIds' | 'taskIds'
  > = useMemo(
    () => ({ planIds: urlPlanIds, taskIds: urlTaskIds }),
    [urlPlanIdsKey, urlTaskIdsKey]
  )
  const drawerFilters: Filters<SampleStatusEnum> = useMemo(
    () => ({ ...filters, planIds: [], taskIds: [] }),
    [filters]
  )
  const filterCount = getFilterCount(drawerFilters)
  const [isFilterReady, setIsFilterReady] = useState(false)
  const urlResultSyncRef = useRef({ initialized: false, value: urlResult })
  const hasPlanContext = urlPlanIds.length > 0 || urlTaskIds.length > 0
  const prevHasPlanContextRef = useRef(false)

  useEffect(() => {
    if (!isFiltersReady) return

    const sync = urlResultSyncRef.current
    if (!sync.initialized || sync.value !== urlResult) {
      const isInitialSync = !sync.initialized
      sync.initialized = true
      sync.value = urlResult

      if (
        (!isInitialSync || urlResult != null) &&
        filters.result !== urlResult
      ) {
        setFilters((current) => ({ ...current, result: urlResult }))
        return
      }
    }

    if (filters.result === urlResult) return

    const params = new URLSearchParams(location.search)
    if (filters.result != null) {
      params.set('result', filters.result)
    } else {
      params.delete('result')
    }
    urlResultSyncRef.current.value = filters.result
    navigate({ search: params.toString() }, { replace: true })
  }, [
    filters.result,
    isFiltersReady,
    location.search,
    navigate,
    setFilters,
    urlResult
  ])

  useEffect(() => {
    if (!isFiltersReady) return

    const prevHasPlanContext = prevHasPlanContextRef.current
    const hasEnteredPlanContext = hasPlanContext && !prevHasPlanContext
    const hasLeftPlanContext = !hasPlanContext && prevHasPlanContext

    if ((hasEnteredPlanContext || hasLeftPlanContext) && filterCount > 0) {
      setFilters({ ...getEmptySampleFilters(), result: urlResult })
    }

    prevHasPlanContextRef.current = hasPlanContext
  }, [filterCount, hasPlanContext, isFiltersReady, setFilters, urlResult])

  useEffect(() => {
    if (urlPlanIds.length > 0 || urlTaskIds.length > 0) {
      setPagination((prev) =>
        prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }
      )
    }
  }, [urlPlanIdsKey, urlTaskIdsKey])

  const planIds = useMemo(() => urlPlanIds.map(Number), [urlPlanIdsKey])
  const taskIds = useMemo(() => urlTaskIds.map(Number), [urlTaskIdsKey])
  const activePlanId =
    planIds?.length === 1 && taskIds.length === 0 ? planIds[0] : undefined
  const activeTaskId =
    taskIds.length > 0 && planIds.length === 0 ? taskIds[0] : undefined
  const { data: contextPlan } = useGetPlanByIdQuery(activePlanId)
  const { data: contextTask } = useGetTaskByIdQuery(activeTaskId)
  const productIds = useMemo(
    () => filters.productIds?.map(Number),
    [filters.productIds]
  )
  const matrixIds = useMemo(
    () => filters.matrixIds?.map(Number),
    [filters.matrixIds]
  )
  const sampntTypeIds = useMemo(
    () => filters.sampntTypeIds?.map(Number),
    [filters.sampntTypeIds]
  )
  const substanceIds = useMemo(
    () => filters.substanceIds?.map(Number),
    [filters.substanceIds]
  )

  const [searchValue, setSearchValue] = useState<string>('')
  const sampleIds = useSampleIds(searchValue)
  const isSearchMode = sampleIds.length > 0
  const hasProducerSearch = (filters.producerSearch?.trim().length ?? 0) > 0
  const isProducerSearchTableMode =
    tabIndex === 0 && hasProducerSearch && !isSearchMode

  // Auto-switch from map when entering search mode.
  useEffect(() => {
    if (isSearchMode && screenView === 'map') setScreenView('table')
  }, [isSearchMode, screenView])

  const ownerIds = useMemo(() => getOwnerIdsFromFilters(filters), [filters])

  const sortParam = useMemo(() => {
    if (sorting.length !== 1) return undefined
    const direction = sorting[0].desc ? 'desc' : 'asc'
    return `${sorting[0].id},${direction}`
  }, [sorting])

  const samplesQueryParams: GetSamplesQueryParams = useMemo(() => {
    // In search mode only apply sampleIds, ignore all other filters
    if (isSearchMode) {
      return {
        isEnabled: tabIndex === 0 && !isProducerSearchTableMode,
        sampleIds,
        page: pagination.pageIndex,
        size: pagination.pageSize,
        sort: sortParam
      }
    }
    return {
      isEnabled: tabIndex === 0 && !isProducerSearchTableMode,
      ownerScope: 'subtree',
      planIds: planIds,
      taskIds: taskIds,
      ownerIds: ownerIds?.map(String),
      productIds: productIds,
      matrixIds: matrixIds,
      sampntTypeIds: sampntTypeIds,
      sampleIds: sampleIds,
      producerSearch: filters.producerSearch?.trim() || undefined,
      substanceIds: substanceIds,
      status: filters.status,
      result: filters.result,
      fromDate: filters.fromDate ?? undefined,
      toDate: filters.toDate ?? undefined,
      page: pagination.pageIndex,
      size: pagination.pageSize,
      sort: sortParam
    }
  }, [
    isSearchMode,
    isProducerSearchTableMode,
    tabIndex,
    planIds,
    taskIds,
    ownerIds,
    productIds,
    matrixIds,
    sampntTypeIds,
    sampleIds,
    filters.producerSearch,
    substanceIds,
    filters.status,
    filters.result,
    filters.fromDate,
    filters.toDate,
    pagination.pageIndex,
    pagination.pageSize,
    sortParam
  ])

  const {
    data: samplePage,
    isLoading: isLoadingSamples,
    isFetching: isFetchingSamples,
    isError: isErrorSamples,
    isPlaceholderData
  } = useGetSamplesQuery(samplesQueryParams)
  const {
    samples: allSamples,
    isLoading,
    isError: isErrorAllSamples,
    progress
  } = useAllSamples({
    isEnabled:
      tabIndex === 1 || (tabIndex === 0 && hasProducerSearch && !isSearchMode),
    ownerScope: 'subtree',
    ownerIds,
    filters: {
      ...drawerFilters,
      ...contextFilters,
      result: drawerFilters.result
    },
    sort: sortParam
  })

  const pagedFilteredSamples = useMemo(() => {
    if (!(tabIndex === 0 && hasProducerSearch && !isSearchMode)) return []

    const start = pagination.pageIndex * pagination.pageSize
    const end = start + pagination.pageSize
    return allSamples.slice(start, end)
  }, [
    allSamples,
    hasProducerSearch,
    isSearchMode,
    pagination.pageIndex,
    pagination.pageSize,
    tabIndex
  ])

  const samples =
    tabIndex === 0 && hasProducerSearch && !isSearchMode
      ? pagedFilteredSamples
      : (samplePage?.content ?? [])
  const rowCount =
    tabIndex === 0 && hasProducerSearch && !isSearchMode
      ? allSamples.length
      : (samplePage?.totalElements ?? 0)
  const [showOnlyAccuratePositions, setShowOnlyAccuratePositions] =
    useState(true)
  const mapSamples = useMemo(
    () =>
      showOnlyAccuratePositions
        ? (allSamples?.filter((s) => s.accuratePosition) ?? [])
        : allSamples,
    [allSamples, showOnlyAccuratePositions]
  )
  const visibleMapSamples = useMemo(
    () => mapSamples.filter((sample) => sample.geometry?.coordinates),
    [mapSamples]
  )
  const sampleHitCount = tabIndex === 0 ? rowCount : visibleMapSamples.length
  const isLoadingFilterResultCount =
    tabIndex === 0 ? !isProducerSearchTableMode && isFetchingSamples : isLoading
  const isInitialProducerSearchLoading =
    isProducerSearchTableMode && isLoading && samples.length === 0

  const tableEmptyCondition = isProducerSearchTableMode
    ? isLoading
    : isLoadingSamples
  const isTableEmpty =
    tabIndex === 0 &&
    !tableEmptyCondition &&
    !isPlaceholderData &&
    samples.length === 0
  const isError = isErrorSamples || isErrorAllSamples
  let contextTitle = ''
  if (activePlanId != null) {
    contextTitle = contextPlan?.description ?? `plan ${activePlanId}`
  } else if (activeTaskId != null) {
    const taskTitle = getTaskTitle(contextTask)
    contextTitle = taskTitle === '-' ? `oppgave ${activeTaskId}` : taskTitle
  }
  const contextType = activePlanId != null ? 'planen' : 'oppgaven'
  const contextTaskPlanId = contextTask?.planIds?.[0]
  const contextLinkTo =
    activePlanId != null
      ? `/plans/${activePlanId}`
      : activeTaskId != null && contextTaskPlanId != null
        ? `/plans/${contextTaskPlanId}/tasks/${activeTaskId}`
        : undefined
  const contextFilterCount = urlPlanIds.length + urlTaskIds.length
  const hasContextFilter = contextFilterCount > 0
  const isFilterDrawerFiltering = filterCount > 0

  const updateFilters = (updatedFilters: SetStateAction<typeof filters>) => {
    setFilters(updatedFilters)
    setPagination((prev) => ({ ...prev, pageIndex: 0 }))
  }

  const updateDrawerFilters = (
    updatedFilters: SetStateAction<Filters<SampleStatusEnum>>
  ) => {
    const nextFilters =
      typeof updatedFilters === 'function'
        ? updatedFilters(drawerFilters)
        : updatedFilters

    updateFilters({ ...nextFilters, planIds: [], taskIds: [] })
  }

  const resetFilters = () => {
    updateFilters(getEmptySampleFilters())
  }

  return (
    <AppLayout
      useMobileBar={false}
      sidebar={<SideBarLayout />}
      isLoading={isLoadingSamples}
      isError={isError}
      topBar={
        <TopBar logo={<AppLogo />} title='Prøver' showNotificationBell={true} />
      }
      isFullWidth
      contentLayout='none'
    >
      <Mtds.Grid
        data-gap='8'
        style={{ height: '100%', gridTemplateRows: 'auto 1fr' }} // To correctly size the map
      >
        <Mtds.Grid data-gap='8'>
          <ScreenHeader
            title='Prøver'
            hideDescriptionBehindHelpText
            description={
              <p>
                Her finner du en oversikt over prøver. Bruk filtre for å
                avgrense listen, eller søk etter bestemte posenummer adskilt med
                komma. Resultater vises fortløpende mens du skriver.
              </p>
            }
          />
          {!isSearchMode && hasContextFilter && (
            <Mtds.Alert data-color='info'>
              <Mtds.Heading>Avgrenset liste</Mtds.Heading>
              <p>
                Listen viser kun prøver som hører til {contextType}{' '}
                <strong>{contextTitle}</strong>. Bruk filtrene under hvis du vil
                avgrense visningen ytterligere.
              </p>
              <Mtds.Flex
                data-align='center'
                data-gap='2'
                data-wrap
                className='mt-4'
              >
                {contextLinkTo != null && (
                  <Mtds.Button
                    as={RouterLink}
                    to={contextLinkTo}
                    data-variant='primary'
                  >
                    <ArrowCounterClockwiseIcon />
                    Gå tilbake til {contextType}
                  </Mtds.Button>
                )}
                <Mtds.Button
                  type='button'
                  data-variant='secondary'
                  onClick={() => navigate('/samples')}
                >
                  <XCircleIcon />
                  Fjern avgrensning
                </Mtds.Button>
              </Mtds.Flex>
            </Mtds.Alert>
          )}
          <Mtds.Grid
            data-gap='4'
            className='md:grid-cols-[minmax(0,1fr)_auto] md:items-center'
          >
            <Mtds.Flex data-align='center' data-wrap>
              <Mtds.Input
                className='w-full md:max-w-xl'
                type='search'
                data-cy='search-input'
                aria-label='Søk på posenummer'
                placeholder='Søk på posenummer'
                value={searchValue}
                onChange={({ target }) => setSearchValue(target.value)}
              />
              {!isSearchMode && (
                <FilterDrawer
                  title='Filtrer prøver'
                  isFiltering={isFilterDrawerFiltering}
                  applyLabel={`Vis ${sampleHitCount} treff`}
                  isApplyLoading={isLoadingFilterResultCount}
                  showApply={isFilterReady}
                  filterCount={filterCount}
                  onReset={resetFilters}
                >
                  <SampleFilterForm
                    filters={drawerFilters}
                    includeRegionFilter
                    setFilters={updateDrawerFilters}
                    onReadyChange={setIsFilterReady}
                  />
                </FilterDrawer>
              )}
            </Mtds.Flex>
            {!isSearchMode && (
              <Mtds.Flex
                data-align='center'
                data-justify='end'
                data-wrap
                className='justify-self-start md:justify-self-end'
              >
                <Mtds.Togglegroup
                  {...({ focusgroup: 'radiogroup' } as Record<string, string>)}
                  aria-label='Velg visning'
                >
                  <Mtds.Togglegroup.Item
                    name='view'
                    value='table'
                    checked={screenView === 'table'}
                    onChange={() => setScreenView('table')}
                  >
                    <ListBulletsIcon />
                    Tabell
                  </Mtds.Togglegroup.Item>
                  <Mtds.Togglegroup.Item
                    name='view'
                    value='map'
                    checked={screenView === 'map'}
                    onChange={() => setScreenView('map')}
                  >
                    <MapTrifoldIcon />
                    Kart
                  </Mtds.Togglegroup.Item>
                </Mtds.Togglegroup>
              </Mtds.Flex>
            )}
          </Mtds.Grid>
          {!isSearchMode && (
            <SampleFilterChips
              filters={drawerFilters}
              isFiltering={isFilterDrawerFiltering}
              setFilters={updateDrawerFilters}
            />
          )}
        </Mtds.Grid>
        {tabIndex === 0 && (
          <div>
            {isInitialProducerSearchLoading && (
              <Mtds.Grid data-gap='2' aria-busy='true'>
                <Mtds.Spinner>Henter prøver...</Mtds.Spinner>
              </Mtds.Grid>
            )}
            {!isInitialProducerSearchLoading && !isTableEmpty && (
              <SamplesView
                mode='table'
                samples={samples ?? []}
                table={{
                  grouping: [],
                  rowCount,
                  pagination,
                  setPagination,
                  sorting,
                  setSorting
                }}
              />
            )}
            {isTableEmpty && (
              <EmptyState
                title={filterCount > 0 ? 'Ingen treff' : 'Ingen prøver'}
                description={
                  filterCount > 0
                    ? 'Prøv andre filtervalg.'
                    : 'Ingen prøver å vise.'
                }
              />
            )}
          </div>
        )}
        {!isSearchMode && tabIndex === 1 && (
          <MapSamplesView
            samples={visibleMapSamples}
            loadingProgress={progress}
            isLoading={isLoading}
            topRightControl={
              <Mtds.Field
                as='input'
                label='Vis kun nøyaktige posisjoner'
                type='checkbox'
                checked={showOnlyAccuratePositions}
                onChange={(e) => setShowOnlyAccuratePositions(e.target.checked)}
              />
            }
          />
        )}
      </Mtds.Grid>
    </AppLayout>
  )
}
